import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSessionQuery } from "../../features/auth/hooks";
import { clientManagementApi } from "../../features/admin/settings/api/clientManagement";
import type { ClientSummary } from "../../features/admin/settings/api/clientManagement";
import { qk } from "../../lib/queryKeys";

const STORAGE_KEY = "peak4_selected_client_id";

interface ClientScopeContextValue {
  isCentralAdmin: boolean;
  clients: ClientSummary[];
  selectedClientId: string;
  setSelectedClientId: (clientId: string) => void;
  effectiveClientId: string | null;
}

const ClientScopeContext = createContext<ClientScopeContextValue | null>(null);

export const ClientScopeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { data: session } = useSessionQuery();
  const isCentralAdmin = session?.user?.is_central_admin ?? false;
  const userClientId = session?.user?.client_id ?? null;
  const [selectedClientId, setSelectedClientId] = useState(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem(STORAGE_KEY) || "";
  });

  const clientsQuery = useQuery({
    queryKey: qk.clients(),
    queryFn: () => clientManagementApi.listClients(),
    enabled: isCentralAdmin,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (selectedClientId) {
      localStorage.setItem(STORAGE_KEY, selectedClientId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [selectedClientId]);

  const value = useMemo<ClientScopeContextValue>(() => {
    const effectiveClientId = isCentralAdmin
      ? selectedClientId || null
      : userClientId;
    return {
      isCentralAdmin,
      clients: clientsQuery.data?.data ?? [],
      selectedClientId,
      setSelectedClientId,
      effectiveClientId,
    };
  }, [
    clientsQuery.data?.data,
    isCentralAdmin,
    selectedClientId,
    userClientId,
  ]);

  return (
    <ClientScopeContext.Provider value={value}>
      {children}
    </ClientScopeContext.Provider>
  );
};

export const useClientScope = () => {
  const context = useContext(ClientScopeContext);
  if (!context) {
    throw new Error("useClientScope must be used within ClientScopeProvider");
  }
  return context;
};
